'use client';

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ComponentPropsWithoutRef,
    type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import { ComponentDataProvider, Region } from '@/components/region';
import { useNavigate } from '@/hooks/use-navigate';
import { cn } from '@/lib/utils';
import {
    contentSlotRegionHasComponents,
    type ContentSlotPageWithComponentData,
} from '@/extensions/snk/lib/content-slot-page';
import { getMenuOverrideItems } from '@/extensions/snk/lib/menu-item-list-overrides';
/** @sfdc-extension-line SNK */
import {
    BRANDS_CATEGORY_ID,
    filterBrandsCategoryTreeForMenu,
    resolveBrandsCategoryForBurgerMenu,
    type BrandsCuratedVariant,
} from '@/extensions/snk/lib/brands-utils';
/** @sfdc-extension-line SNK */
import {
    getActiveCategoryFromStack,
    getNavigationLevelKey,
    type BurgerMenuStackEntry,
} from '@/extensions/snk/lib/burger-menu-navigation-stack';
/** @sfdc-extension-line SNK */
import {
    getCategoryHref,
    getCategorySubmenuSlot,
    GLOBAL_BURGER_MENU_FOOTER_REGION_ID,
    GLOBAL_TRENDING_CAROUSEL_REGION_ID,
    getMenuVisibleChildCategories,
    isCategoryShownInMenu,
    isMenuOverridden,
    mergeDeferredBurgerMenuCategories,
    type CategorySubmenuSlots,
    type MenuItemListOverrides,
} from '@/extensions/snk/lib/utils';
import { BurgerMenuBrands } from '../../molecules/brands';
import { BurgerMenuBrandsCurated } from '../../molecules/brands-curated';
import { BurgerMenuNavigation } from '../../molecules/navigation';
import { BurgerMenuNavigationLevel, type BurgerMenuNavigationDirection } from '../../molecules/navigation-level';
import { BurgerMenuFooterSkeleton, BurgerMenuTrendingSkeleton } from '../../molecules/skeleton';
import { BurgerMenuSubmenuPdContent } from '../../molecules/submenu-pd-content';
import { BurgerMenuTopStrip } from '../../molecules/top-strip';

type BurgerMenuPanelContentProps = {
    categories: ShopperProducts.schemas['Category'][];
    subCategoriesPromise?: Promise<ShopperProducts.schemas['Category'][]>;
    menuOverrides: MenuItemListOverrides;
    categorySubmenuSlots: CategorySubmenuSlots;
    contentSlots?: ContentSlotPageWithComponentData;
    isContentSlotsLoading: boolean;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const BurgerMenuPanelContent = ({
    categories,
    subCategoriesPromise,
    menuOverrides,
    categorySubmenuSlots,
    contentSlots,
    isContentSlotsLoading,
    open,
    onOpenChange,
}: BurgerMenuPanelContentProps): ReactElement => {
    const navigate = useNavigate();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [navigationStack, setNavigationStack] = useState<BurgerMenuStackEntry[]>([]);
    const [navigationDirection, setNavigationDirection] = useState<BurgerMenuNavigationDirection | null>(null);
    const [deferredCategories, setDeferredCategories] = useState<ShopperProducts.schemas['Category'][] | undefined>();

    useEffect(() => {
        if (!subCategoriesPromise) {
            setDeferredCategories(undefined);
            return;
        }

        let cancelled = false;

        void subCategoriesPromise
            .then((subs) => {
                if (!cancelled) {
                    setDeferredCategories(subs);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDeferredCategories(undefined);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [subCategoriesPromise]);

    const enrichedRootCategories = useMemo(
        () => mergeDeferredBurgerMenuCategories(categories, deferredCategories),
        [categories, deferredCategories]
    );

    const brandsCategory = useMemo(() => {
        const resolved = resolveBrandsCategoryForBurgerMenu(enrichedRootCategories, deferredCategories);
        return resolved && isCategoryShownInMenu(resolved) ? filterBrandsCategoryTreeForMenu(resolved) : undefined;
    }, [enrichedRootCategories, deferredCategories]);
    const rootCategories = useMemo(
        () => enrichedRootCategories.filter((category) => category.id !== BRANDS_CATEGORY_ID),
        [enrichedRootCategories]
    );

    const resolveEnrichedCategory = useCallback(
        (category: ShopperProducts.schemas['Category']) =>
            enrichedRootCategories.find((entry) => entry.id === category.id) ?? category,
        [enrichedRootCategories]
    );

    const activeEntry = navigationStack.at(-1);
    const activeCategory = getActiveCategoryFromStack(navigationStack);
    const resolvedActiveCategory = useMemo(
        () => (activeCategory ? resolveEnrichedCategory(activeCategory) : undefined),
        [activeCategory, resolveEnrichedCategory]
    );
    const isRootLevel = navigationStack.length === 0;
    const navigationLevelKey = getNavigationLevelKey(navigationStack);
    const visibleOverrideItems = useMemo(() => {
        if (!resolvedActiveCategory || !isMenuOverridden(resolvedActiveCategory)) {
            return undefined;
        }
        return getMenuOverrideItems(resolvedActiveCategory.id, menuOverrides);
    }, [resolvedActiveCategory, menuOverrides]);

    const visibleCategories = useMemo(() => {
        if (visibleOverrideItems) {
            return [];
        }
        if (resolvedActiveCategory) {
            const children = getMenuVisibleChildCategories(resolvedActiveCategory);
            if (children.length > 0) {
                return children;
            }
        }
        return rootCategories;
    }, [resolvedActiveCategory, rootCategories, visibleOverrideItems]);

    const activeCategorySubmenuSlot = useMemo(() => {
        if (!resolvedActiveCategory) {
            return undefined;
        }

        return getCategorySubmenuSlot(resolvedActiveCategory.id, categorySubmenuSlots);
    }, [resolvedActiveCategory, categorySubmenuSlots]);

    const resetNavigation = useCallback(() => {
        setNavigationStack([]);
    }, []);

    useEffect(() => {
        if (!open) {
            resetNavigation();
            setNavigationDirection(null);
        }
    }, [open, resetNavigation]);

    const pushNavigation = useCallback((entry: BurgerMenuStackEntry) => {
        setNavigationDirection('forward');
        setNavigationStack((prev) => [...prev, entry]);
    }, []);

    const handleOpenSubmenu = (category: ShopperProducts.schemas['Category']) => {
        pushNavigation({ type: 'category', category: resolveEnrichedCategory(category) });
    };

    const handleOpenBrands = () => {
        pushNavigation({ type: 'brands' });
    };

    const handleOpenBrandsCurated = (variant: BrandsCuratedVariant) => {
        pushNavigation({ type: 'brands-curated', variant });
    };

    const handleBack = () => {
        setNavigationDirection('back');
        setNavigationStack((prev) => prev.slice(0, -1));
    };

    const handleNavigateToCategory = (categoryId: string) => {
        onOpenChange(false);
        resetNavigation();
        void navigate(getCategoryHref(categoryId));
    };

    const handleScrollToBrandLetter = (sectionId: string) => {
        const container = scrollContainerRef.current;
        const section = container?.querySelector<HTMLElement>(`#${sectionId}`);
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const rootSlotsCollapsedClass = isRootLevel ? undefined : 'pointer-events-none max-h-0 overflow-hidden opacity-0';

    const hasTrendingCarousel = contentSlotRegionHasComponents(contentSlots, GLOBAL_TRENDING_CAROUSEL_REGION_ID);
    const hasFooterContent = contentSlotRegionHasComponents(contentSlots, GLOBAL_BURGER_MENU_FOOTER_REGION_ID);

    const renderNavigationBody = (): ReactElement => {
        if (activeEntry?.type === 'brands' && brandsCategory) {
            return (
                <BurgerMenuBrands
                    brandsCategory={brandsCategory}
                    onBack={handleBack}
                    onCloseMenu={() => onOpenChange(false)}
                    onOpenCurated={handleOpenBrandsCurated}
                    onScrollToLetter={handleScrollToBrandLetter}
                />
            );
        }

        if (activeEntry?.type === 'brands-curated' && brandsCategory) {
            return (
                <BurgerMenuBrandsCurated
                    brandsCategory={brandsCategory}
                    variant={activeEntry.variant}
                    contentSlots={contentSlots}
                    onBack={handleBack}
                    onCloseMenu={() => onOpenChange(false)}
                />
            );
        }

        return (
            <BurgerMenuNavigation
                categories={visibleCategories}
                overrideItems={visibleOverrideItems}
                menuOverrides={menuOverrides}
                activeCategory={resolvedActiveCategory}
                categorySubmenuSlot={activeCategorySubmenuSlot}
                showBrandsEntry={Boolean(brandsCategory)}
                onOpenBrands={handleOpenBrands}
                onOpenSubmenu={handleOpenSubmenu}
                onBack={handleBack}
                onCloseMenu={() => onOpenChange(false)}
                onNavigateToCategory={handleNavigateToCategory}
            />
        );
    };

    const panelContent = (
        <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-background">
            {hasTrendingCarousel || isContentSlotsLoading ? (
                <div
                    data-testid="burger-menu-root-trending-slot"
                    className={cn(rootSlotsCollapsedClass)}
                    aria-hidden={!isRootLevel || undefined}>
                    {hasTrendingCarousel ? (
                        <BurgerMenuSubmenuPdContent fallback={<BurgerMenuTrendingSkeleton />}>
                            <Region
                                page={contentSlots ?? null}
                                regionId={GLOBAL_TRENDING_CAROUSEL_REGION_ID}
                                fallbackElement={<BurgerMenuTrendingSkeleton />}
                                errorElement={null}
                            />
                        </BurgerMenuSubmenuPdContent>
                    ) : (
                        <BurgerMenuTrendingSkeleton />
                    )}
                </div>
            ) : null}

            <BurgerMenuNavigationLevel direction={navigationDirection} levelKey={navigationLevelKey}>
                {renderNavigationBody()}
            </BurgerMenuNavigationLevel>

            {hasFooterContent || isContentSlotsLoading ? (
                <div
                    data-testid="burger-menu-root-footer-slot"
                    className={cn('flex flex-col', rootSlotsCollapsedClass)}
                    aria-hidden={!isRootLevel || undefined}>
                    {hasFooterContent ? (
                        <BurgerMenuSubmenuPdContent fallback={<BurgerMenuFooterSkeleton />}>
                            <Region
                                page={contentSlots ?? null}
                                regionId={GLOBAL_BURGER_MENU_FOOTER_REGION_ID}
                                className="flex flex-col"
                                fallbackElement={<BurgerMenuFooterSkeleton />}
                                errorElement={null}
                            />
                        </BurgerMenuSubmenuPdContent>
                    ) : (
                        <BurgerMenuFooterSkeleton />
                    )}
                </div>
            ) : null}
        </div>
    );

    if (contentSlots?.componentData) {
        return <ComponentDataProvider value={contentSlots.componentData}>{panelContent}</ComponentDataProvider>;
    }

    return panelContent;
};

export type BurgerMenuPanelProps = ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu> & {
    rootCategoryPromise?: Promise<ShopperProducts.schemas['Category']>;
    subCategoriesPromise?: Promise<ShopperProducts.schemas['Category'][]>;
    menuOverrides: MenuItemListOverrides;
    categorySubmenuSlots: CategorySubmenuSlots;
    contentSlots?: ContentSlotPageWithComponentData;
    isContentSlotsLoading: boolean;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export const BurgerMenuPanel = ({
    rootCategoryPromise,
    subCategoriesPromise,
    menuOverrides,
    categorySubmenuSlots,
    contentSlots,
    isContentSlotsLoading,
    open,
    onOpenChange,
    ...menuProps
}: BurgerMenuPanelProps): ReactElement => {
    const { t } = useTranslation('header');

    return (
        <div className="flex min-h-0 flex-1 flex-col" aria-busy={isContentSlotsLoading} data-testid="burger-menu-panel">
            <div className="shrink-0">
                <BurgerMenuTopStrip />
            </div>
            {isContentSlotsLoading ? (
                <p className="sr-only">{t('burgerMenu.loading', 'Loading navigation menu')}</p>
            ) : null}
            {rootCategoryPromise ? (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <WithCategoryNavigationMenu
                        resolve={rootCategoryPromise}
                        defer={subCategoriesPromise}
                        {...menuProps}>
                        {({ categories: resolvedCategories }) => (
                            <BurgerMenuPanelContent
                                categories={resolvedCategories}
                                subCategoriesPromise={subCategoriesPromise}
                                menuOverrides={menuOverrides}
                                categorySubmenuSlots={categorySubmenuSlots}
                                contentSlots={contentSlots}
                                isContentSlotsLoading={isContentSlotsLoading}
                                open={open}
                                onOpenChange={onOpenChange}
                            />
                        )}
                    </WithCategoryNavigationMenu>
                </div>
            ) : null}
        </div>
    );
};
